import React, {useState, useEffect, useRef} from 'react';
import {Animated, Easing, Platform} from 'react-native';
import styled from 'styled-components/native';
import {LoadingDotsProps} from '../../interfaces/components/loadingDots';

const defaultColors = ['#4dabf7', '#3bc9db', '#38d9a9', '#69db7c'];

const LoadingContainer = styled(Animated.View)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-evenly;
`;

const AnimatedView = styled(Animated.View)``;

const Dot = styled(Animated.View)<{
  width: number;
  height: number;
  borderRadius: number;
  backgroundColor: string;
}>`
  width: ${props => props.width}px;
  height: ${props => props.height}px;
  border-radius: ${props => props.borderRadius}px;
  background-color: ${props => props.backgroundColor};
  margin-right: 3px;
`;

const LoadingDots: React.FC<LoadingDotsProps> = ({
  dots = 4,
  colors = defaultColors,
  size = 10,
  bounceHeight = 10,
  borderRadius,
  components = null,
}) => {
  const [animations, setAnimations] = useState<Animated.Value[]>([]);
  const [reverse, setReverse] = useState<boolean>(false);
  const opacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const dotAnimations: Animated.Value[] = [];
    const animationsAmount =
      components && Array.isArray(components) ? components.length : dots;
    for (let i = 0; i < animationsAmount; i++) {
      dotAnimations.push(new Animated.Value(0));
    }
    setAnimations(dotAnimations);
  }, [components, dots]);

  useEffect(() => {
    if (animations.length === 0) {
      return;
    }
    loadingAnimation(animations, reverse);
    appearAnimation();
  }, [animations, reverse]);

  function appearAnimation() {
    Animated.timing(opacity, {
      toValue: 1,
      easing: Easing.ease,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }

  function floatAnimation(
    node: Animated.Value,
    reverseY: boolean,
    delay: number,
  ) {
    const floatSequence = Animated.sequence([
      Animated.timing(node, {
        toValue: reverseY ? bounceHeight : -bounceHeight,
        easing: Easing.bezier(0.41, -0.15, 0.56, 1.21),
        delay,
        useNativeDriver: Platform.OS !== 'web',
      }),
      Animated.timing(node, {
        toValue: reverseY ? -bounceHeight : bounceHeight,
        easing: Easing.bezier(0.41, -0.15, 0.56, 1.21),
        delay,
        useNativeDriver: Platform.OS !== 'web',
      }),
      Animated.timing(node, {
        toValue: 0,
        delay,
        useNativeDriver: Platform.OS !== 'web',
      }),
    ]);
    return floatSequence;
  }

  function loadingAnimation(nodes: Animated.Value[], reverseY: boolean) {
    Animated.parallel(
      nodes.map((node, index) => floatAnimation(node, reverseY, index * 100)),
    ).start(() => {
      setReverse(!reverse);
    });
  }

  return (
    <LoadingContainer style={{opacity}}>
      {animations.map((animation, index) =>
        components ? (
          <AnimatedView
            key={`loading-anim-${index}`}
            style={{transform: [{translateY: animation}]}}>
            {components[index]}
          </AnimatedView>
        ) : (
          <Dot
            key={`loading-anim-${index}`}
            style={{transform: [{translateY: animation}]}}
            width={size}
            height={size}
            borderRadius={borderRadius || size / 2}
            backgroundColor={colors[index % colors.length]}
          />
        ),
      )}
    </LoadingContainer>
  );
};

export default LoadingDots;
